import { InvestingType } from './kyc.data.models';

export interface UserPhone {
  phoneNumber?: string | undefined;
  userId?: string | undefined;
  nationalId?: string | undefined;
}
export enum GrpcLanguage {
  Ar = 'Ar',
  En = 'En',
}
export enum ClientType {
  Investor = 0,
  Borrower = 1,
  Supplier = 2,
}

export interface ValidateCredentialsInput {
  email: string;
  password: string;
  userType: number;
}

/** Investor login step 1: encrypted mobile number; the API sends the login OTP on success. */
export interface InvestorValidateCredentialsInput {
  phoneNumber: string;
}

export interface InvestorCredentialsResult {
  phoneNumber: string;
}

/** Investor login step 2: encrypted mobile number + OTP. */
export interface InvestorLoginInput {
  phoneNumber: string;
  otp: string;
  rememberMe: boolean;
}

export interface GenerateOtpInput {
  phoneNumber: string;
  operationType: number;
}

export interface LoginInput {
  email: string;
  password: string;
  userType: number;
  otp: string;
  rememberMe: boolean;
}

export interface LoginResponse {
  token: string;
  refreshToken: string;
  expiresIn: number;
  userId: string;
  userName: string;
  roles: string[];
}

export enum MobileDeviceType {
  Android = 'Android',
  IOS = 'IOS',
}

export interface UserPassword {
  password?: string | undefined;
  confirmPassword?: string | undefined;
  phoneNumber?: string | undefined;
  otp?: string | undefined;
}

export interface UserRegisterInput {
  phoneNumber?: string | undefined;
  email?: string | undefined;
  nationalID?: string | undefined;
  birthDate?: string | undefined;
  language?: GrpcLanguage;
  investTypeId?: string;
  dateType: 'hijri' | 'gregorian';
}
export interface RegistrationData {
  serviceType: ClientType | null;
  /** Investor web registration only: company investors add the CR step. */
  investorType?: 'individual' | 'company' | null;
  userName: string | null;
  phone: string | null;
  crn: string | null;
  unitedNationsNumber: string | null;
  isBoardMember?: boolean | null;
  legalEntityIdentifierNumber?: string | null;
  crName?: string | null;
  commercialRegistrationExpiryDate?: string | null;
  /** CR / establishment date (optional; sent as CommercialRegistrationIssueDate) */
  commercialRegistrationIssueDate?: string | null;
  companySectorId?: string | null;
  companyTypeId?: string | null;
  commercialDocument?: string | null;
  companyNationalAddress?: {
    buildingNo: string | null;
    extraNo: string | null;
    streetName: string | null;
    district: string | null;
    city: string | null;
    postalCode: string | null;
    unitNo: number | null;
  } | null;
  personal: {
    nid: string | null;
    /**
     * Sent exactly as the user picked it: Hijri (`iYYYY-iMM-iDD`) when `dateType`
     * is `hijri`, Gregorian (`YYYY-MM-DD`) otherwise. Never converted client-side.
     */
    birthdate: string | null;
    /** Calendar `birthdate` is expressed in. */
    dateType?: 'hijri' | 'gregorian' | null;
    fullName?: string | null;
    fullNameAr?: string | null;
    fullNameEn?: string | null;
    /** ISO alpha-3 country id (e.g. SAU). */
    nationalityId?: string | null;
    address: {
      buildingNo: null;
      additionalNo: null;
      streetName: null;
      district: null;
      city: null;
      postalCode: null;
      unitNo: null;
    };
  };

  /** Set after successful verify registration email OTP (required for Supplier Register). */
  emailVerificationToken?: string | null;
  /** Set after successful verify registration phone OTP (required for Borrower Register). */
  phoneVerificationToken?: string | null;
  /** Epoch ms when the phone OTP was verified; the server token lives 30 min. */
  phoneVerifiedAt?: number | null;
  /** Returned by CheckNafath once COMPLETED; checked by the API before the account is created. */
  nafath?: {
    nafathVerificationId: string | null;
  } | null;
  account: {
    email: string | null;
    password: string | null;
  };
  address?: {
    buildingNo: string | null;
    extraNo: string | null;
    streetName: string | null;
    district: string | null;
    city: string | null;
    postalCode: string | null;
    unitNo: number | null;
  };
}

export interface RefreshTokenInputDto {
  phoneNumber?: string | undefined;
  refreshToken?: string | undefined;
}

export interface UserOTP {
  otp?: string | undefined;
  phoneNumber?: string | undefined;
  language?: GrpcLanguage;
}
export interface LoginResultDto {
  currentUsedInvestorAccountId: string;
  email: string;
  phoneNumber: string;
  nationalId: string;
  userTypeId: string;
  userType?: ClientType | string;
  roles?: string[];
  investTypeId: InvestingType;
  userId: string;
  token: string;
  arabicName: string;
  englishName: string;
  isActivated: boolean;
  isSuspended: boolean;
  isIdExpired: boolean;
  birthDate: Date;
  gender: string;
  isCompleteProfile: boolean;
  userKYCStatus: string;
  refreshToken: string;
  expiryTokenInMinutes: number;
  moznUserStatus: string;
  investorDegreeType: string;
  hasProfessionalPendingRequest: boolean;
  hasInstitutionalPendingRequest: boolean;
  expiryTokenDate: Date;
  parentUserId: string;
  currentActiveAccountId: string;
  requireChangePassword?: boolean;
}
